import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import type { OnlyIdRequiredInterface } from '@zaoblako/shared/common/interfaces/only-id-required.interface';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import {
  HelpdeskTaskPersonalAccountCreateDto,
  HelpdeskTaskPersonalAccountCreateInterface
} from '@zaoblako/shared/modules/helpdesk/dto/task/helpdesk-task-personal-account-create.dto';
import { HelpdeskTaskStatusEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.status.enum';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FileService } from '@zaoblako/web/core/services/file.service';
import { PersonalAccountHdTaskService } from '@zaoblako/web/core/services/lk/personal-account-hd-task.service';
import { FilesListComponent } from '@zaoblako/web/shared/components/files-list/files-list.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { HdTaskTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/hd-task-type-single-select/hd-task-type-single-select.component';
import { PersonalAccountExternalUserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/personal-account-external-user-multi-select/personal-account-external-user-multi-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-lk-eu-helpdesk-task-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    CardModule,
    EditorComponent,
    JoinPipe,
    HdTaskTypeSingleSelectComponent,
    ButtonModule,
    FileUploadModule,
    FilesListComponent,
    PersonalAccountExternalUserMultiSelectComponent
  ],
  templateUrl: './lk-eu-helpdesk-task-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LkEuHelpdeskTaskFormComponent
  extends BaseFormComponent<HelpdeskTaskPersonalAccountCreateInterface>
  implements OnInit
{
  @Input() public readonly index: number;
  public task: HelpdeskTaskPersonalAccountCreateInterface = <any>{};
  public dto = HelpdeskTaskPersonalAccountCreateDto;

  public engine: 'class-transformer' | 'serde' = 'serde';

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  @ViewChild('fileUpload') public fileUpload: FileUpload;
  public filesLoading = false;

  public taskTypes: HelpdeskTaskTypeFindInterface[] = [];

  public get HelpdeskTaskStatusEnum() {
    return HelpdeskTaskStatusEnum;
  }

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly personalAccountHdTaskService: PersonalAccountHdTaskService,
    private readonly fileService: FileService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.personalAccountHdTaskService.getTaskTypes().subscribe((taskTypes) => {
      this.taskTypes = taskTypes;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.personalAccountHdTaskService
      .findById<HelpdeskTaskFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (task) => {
          this.task = task;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveTask() {
    if (this.checkValidate(this.task).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.personalAccountHdTaskService
          .update<HelpdeskTaskPersonalAccountCreateInterface, HelpdeskTaskFindInterface>(this.task)
          .subscribe({
            next: (task) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Заявка №${task?.id} обновлена`);
              this.formStoreService.saveNext(task, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.personalAccountHdTaskService.create<HelpdeskTaskFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Заявка создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/lk/external-user/hd/card/' + task.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(task, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public uploadFiles(data: { files: FileList }) {
    this.filesLoading = true;
    this.cdRef.markForCheck();

    this.fileService.upload(data.files).subscribe({
      next: (files) => {
        this.task.files = this.task.files?.length ? this.task.files.concat(files) : files;
        this.fileUpload.clear();
        this.filesLoading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.filesLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public deleteFile(files: OnlyIdRequiredInterface<FileFindInterface>[], file: FileFindInterface) {
    this.confirmDialogService.confirm({
      message: `Вы действительно хотите удалить файл "${file.name}"?`,
      accept: () => {
        const index = files.indexOf(file);
        files.splice(index, 1);
        this.cdRef.markForCheck();
      }
    });
  }
}
